// @ts-nocheck — Vendored zod schema definitions. tri-portal is still on
// TypeScript 4.9 while zod v4's .d.ts uses `<const T>` syntax that requires
// TS 5.0+. Under 4.9 the generic constraint silently drops, leaving the call
// sites looking like "z.literal expects 0 args" / "z.enum expects 0 args".
// Runtime behavior is unaffected. Remove this pragma once tri-portal is on
// TypeScript 5.
/**
 * Semantic browser actions captured during a recording session.
 *
 * Vendored from `tri-bot/packages/bpm-protocol/src/browserActions.ts`
 * so tri-portal can build without a `file:../tri-bot/…` install-time
 * dependency on a sibling checkout. Tri-bot remains the authoritative
 * source for the protocol — when its schema changes, update this file
 * by hand.
 *
 * Persisted on a BPMN user-task element as the `tri:browserActions`
 * attribute (JSON-encoded). The browser-bot extension's content script
 * captures user interactions as one of these structured actions, never as
 * CSS selectors or DOM ids — the brittleness those introduce is exactly
 * what this design avoids.
 *
 * `runtimeValue` (on `type` and `select`) is a `ValueBinding` chosen by
 * the modeler at recording time:
 *   - `literal`     — use the captured value verbatim every run
 *   - `description` — natural-language pointer the LLM resolves against
 *                     process data at runtime
 *   - `context`     — legacy; resolved via the synthesized __session.*
 *                     entries in processVariables (kept for back-compat
 *                     with old recordings)
 *
 * Backward compatibility: persisted JSON from earlier recordings has
 * `runtimeValue` as a plain string. The schema preprocesses such values
 * into `{ kind: "literal", value: <string> }` on parse so old models
 * keep loading.
 */

import { z } from "zod";

const baseProps = {
  /** Stable id assigned at recording time. */
  id: z.string().min(1),
  /** ISO timestamp of when the action was recorded. */
  recordedAt: z.string().min(1),
  /** Optional human-readable note the modeler can edit. */
  note: z.string().optional(),
};

export const ContextFieldSchema = z.enum([
  "userId",
  "tenantId",
  "locale",
  "timezone",
]);
export type ContextField = "userId" | "tenantId" | "locale" | "timezone";

export type ValueBinding =
  | { kind: "literal"; value: string }
  | { kind: "description"; text: string }
  | { kind: "context"; field: ContextField };

export const ValueBindingSchema = z.preprocess(
  // Legacy coercion: a plain string from older recordings becomes a
  // literal binding. Anything else falls through to the discriminated
  // union below, which will reject malformed objects loudly.
  (raw) => (typeof raw === "string" ? { kind: "literal", value: raw } : raw),
  z.discriminatedUnion("kind", [
    z.object({ kind: z.literal("literal"), value: z.string() }),
    // `text` is allowed to be empty here so in-flight UI state (an
    // action that's just been re-classified to Laufzeit but the modeler
    // hasn't typed the description yet) parses cleanly. The modeler-facing
    // UIs enforce non-empty before allowing save.
    z.object({ kind: z.literal("description"), text: z.string() }),
    z.object({ kind: z.literal("context"), field: ContextFieldSchema }),
  ]),
);

export const BrowserActionSchema = z.discriminatedUnion("kind", [
  z.object({
    ...baseProps,
    kind: z.literal("navigate"),
    url: z.string().url(),
  }),
  z.object({
    ...baseProps,
    kind: z.literal("click"),
    role: z.string().min(1),
    label: z.string().min(1),
    /** Best-effort context: nearest containing landmark or heading name. */
    context: z.string().optional(),
  }),
  z.object({
    ...baseProps,
    kind: z.literal("type"),
    role: z.string().min(1),
    label: z.string().min(1),
    /** The literal characters captured during recording (may be redacted). */
    capturedValue: z.string(),
    /** What the bot will type at runtime — literal / description / context. */
    runtimeValue: ValueBindingSchema,
    context: z.string().optional(),
  }),
  z.object({
    ...baseProps,
    kind: z.literal("select"),
    role: z.string().min(1),
    label: z.string().min(1),
    /** The option text the user picked during recording. */
    option: z.string(),
    runtimeValue: ValueBindingSchema,
    context: z.string().optional(),
  }),
  z.object({
    ...baseProps,
    kind: z.literal("submit"),
    label: z.string().optional(),
    context: z.string().optional(),
  }),
  z.object({
    ...baseProps,
    kind: z.literal("wait"),
    /** Wait for an element with this accessible name to appear. */
    forLabel: z.string().optional(),
    timeoutMs: z.number().int().positive(),
  }),
]);

type BrowserActionBase = {
  id: string;
  recordedAt: string;
  note?: string;
};

export type BrowserAction =
  | (BrowserActionBase & { kind: "navigate"; url: string })
  | (BrowserActionBase & { kind: "click"; role: string; label: string; context?: string })
  | (BrowserActionBase & {
      kind: "type";
      role: string;
      label: string;
      capturedValue: string;
      runtimeValue: ValueBinding;
      context?: string;
    })
  | (BrowserActionBase & {
      kind: "select";
      role: string;
      label: string;
      option: string;
      runtimeValue: ValueBinding;
      context?: string;
    })
  | (BrowserActionBase & { kind: "submit"; label?: string; context?: string })
  | (BrowserActionBase & { kind: "wait"; forLabel?: string; timeoutMs: number });

export const BrowserActionListSchema = z.array(BrowserActionSchema);
export type BrowserActionList = BrowserAction[];

/** Sentinel inserted by the privacy guard in place of redacted values. */
export const REDACTED_PLACEHOLDER = "<REDACTED>" as const;

/** Convenience: the conventional default binding for a freshly-captured
 *  type/select action. */
export function literalBinding(value: string): ValueBinding {
  return { kind: "literal", value };
}
